import { useCallback, useEffect, useRef, useState } from 'react';
import { EXCLUSION_RELATED_CLASSES, TEXT_TAGS } from '../constants';
import {
  type CSSPropertyName,
  processTextElements
} from '../utils/process-text-elements';

const DEBOUNCE_DELAY = 100;
const TEXT_PROPERTIES: CSSPropertyName[] = [
  'fontSize',
  'lineHeight',
  'letterSpacing'
];
const OBSERVED_ATTRIBUTE = 'class';
const TEXT_TAGS_SELECTOR = TEXT_TAGS.join(', ');

export function useTextStyleAttributes() {
  const [processedElements, setProcessedElements] = useState<HTMLElement[]>([]);
  const elementsSetRef = useRef(new Set<HTMLElement>());
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  const updateProcessedElements = useCallback((nodes: Node[]) => {
    elementsSetRef.current.forEach((element) => {
      if (!element.isConnected) {
        elementsSetRef.current.delete(element);
      }
    });

    if (nodes.length === 0) return;

    const previousElements = new Set(elementsSetRef.current);

    processTextElements(nodes, TEXT_PROPERTIES, elementsSetRef.current);

    const hasChangedElements =
      previousElements.size !== elementsSetRef.current.size ||
      Array.from(elementsSetRef.current).some(
        (element) => !previousElements.has(element)
      );

    if (hasChangedElements) {
      setProcessedElements(Array.from(elementsSetRef.current));
    }
  }, []);

  useEffect(() => {
    // Initial processing of all elements
    updateProcessedElements(Array.from(document.body.querySelectorAll('*')));

    // Watch for DOM changes
    const observer = new MutationObserver((mutations) => {
      clearTimeout(debounceTimerRef.current);

      debounceTimerRef.current = setTimeout(() => {
        const nodesToProcess = collectNodesToProcess(mutations);
        updateProcessedElements(nodesToProcess);
      }, DEBOUNCE_DELAY);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeOldValue: true,
      attributeFilter: [OBSERVED_ATTRIBUTE]
    });

    return () => {
      clearTimeout(debounceTimerRef.current);
      observer.disconnect();
    };
  }, [updateProcessedElements]);

  return processedElements;
}

function collectNodesToProcess(mutations: MutationRecord[]): HTMLElement[] {
  const nodes = new Set<HTMLElement>();

  mutations.forEach((mutation) => {
    if (mutation.type === 'attributes') {
      if (
        mutation.target instanceof HTMLElement &&
        isRelevantClassMutation(mutation)
      ) {
        collectTextNodes(mutation.target, nodes);
      }

      return;
    }

    if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) {
      return;
    }

    // Process parent element (handles dangerouslySetInnerHTML with text nodes)
    if (mutation.target instanceof HTMLElement) {
      collectTextNodes(mutation.target, nodes);
    }

    // Process added elements and their descendants
    mutation.addedNodes.forEach((node) => {
      if (node instanceof HTMLElement) {
        collectTextNodes(node, nodes);
      }
    });
  });

  return Array.from(nodes);
}

function isRelevantClassMutation(mutation: MutationRecord) {
  if (
    mutation.type !== 'attributes' ||
    mutation.attributeName !== OBSERVED_ATTRIBUTE ||
    !(mutation.target instanceof HTMLElement)
  ) {
    return false;
  }

  const currentClassName = mutation.target.className;
  const previousClassName = mutation.oldValue ?? '';

  return EXCLUSION_RELATED_CLASSES.some(
    (className) =>
      currentClassName.includes(className) ||
      previousClassName.includes(className)
  );
}

function collectTextNodes(root: HTMLElement, nodes: Set<HTMLElement>) {
  if (TEXT_TAGS.includes(root.tagName.toLowerCase())) {
    nodes.add(root);
  }

  root.querySelectorAll<HTMLElement>(TEXT_TAGS_SELECTOR).forEach((element) => {
    nodes.add(element);
  });
}
